import { FormControl, FormHelperText, FormLabel, Radio, RadioGroup, Typography } from "@mui/joy";
import { useController, useFormContext } from "react-hook-form";

type RadioOption<T = string> = {
  value: T;
  label: string;
  helpText?: string;
};

type RadioGroupInputProps<T = string> = {
  name: string;
  label: string;
  options: RadioOption<T>[];
  disabled?: boolean;
  required?: boolean;
};

const RadioGroupInput = <T extends string | boolean = string>({
  name,
  label,
  options,
  disabled = false,
  required = false,
}: RadioGroupInputProps<T>) => {
  const { control } = useFormContext();
  const { field, fieldState } = useController({
    name,
    control,
  });

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const stringValue = event.target.value;
    const option = options.find((opt) => String(opt.value) === stringValue);
    if (option) {
      field.onChange(option.value);
    }
  };

  const displayValue = String(field.value ?? "");

  return (
    <FormControl error={Boolean(fieldState.error)} required={required}>
      <FormLabel>{label}</FormLabel>
      <RadioGroup value={displayValue} onChange={handleChange} onBlur={field.onBlur}>
        {options.map((option) => (
          <Radio
            key={String(option.value)}
            value={String(option.value)}
            disabled={disabled}
            label={
              <div>
                <Typography component="span">{option.label}</Typography>
                {option.helpText && (
                  <Typography
                    level="body-sm"
                    sx={{ display: "block", color: "neutral.600", mt: 0.25 }}
                  >
                    {option.helpText}
                  </Typography>
                )}
              </div>
            }
          />
        ))}
      </RadioGroup>
      {fieldState.error?.message && <FormHelperText>{fieldState.error.message}</FormHelperText>}
    </FormControl>
  );
};

export default RadioGroupInput;
